import {useState, useEffect, useCallback} from 'react';
import {EMPTY_ARRAY} from '@shared/ducks/constants';
import toQueryString from './utils/toQueryString';
import reportError from './domain/errors/reportError';
import {HttpStatus} from './domain/request/constants';
import fetchOptions from './domain/request/fetchOptions';
import usePrevious from './utils/usePrevious';

const identity = x => x;

const DEFAULT_SENDER = 'Verkkopalvelu';

/**
 * Hook for fetch requests
 * @param opts Object with options:
 *  - sender: Sender string for error handling (optional)
 *  - disableErrorHandling: if true won't raise errors to generic error handler
 *  - allowedErrorCodes: array of numerical http status codes that won't get raised to error handler
 *
 * An array can be provided also, requests will be done sequentially. This is best used for
 * fire-and-forget special occasions.
 *
 * Returned items:
 *  - doRequest: function to call to trigger request, supply options as object:
 *    - method: http verb (default GET)
 *    - url: request url, mandatory
 *    - query: query parameters as object, will be encoded to url
 *    - body: request body data
 *    - json: whether to parse response as json (default true)
 *    - setter: additional setter function for returned data, called on success
 *  - data: contains the data when request finished
 *  - isLoading: true while request is pending
 *  - requestFinished: timestamp which is updated on request finish (loading stopped)
 *  - error: contains error if request failed
 *  - attempted: true if request has been triggered at least once
 */
const useFetch = (opts = {}) => {
  const sender = opts.sender || DEFAULT_SENDER;
  const disableErrorHandling = !!opts.disableErrorHandling;

  // Not added to useEffect deps because array instance might change all the time and
  // given config is not expected to change during hook lifetime
  const allowedErrorCodes = [...(opts.allowedErrorCodes || EMPTY_ARRAY)];

  const [data, setData] = useState(null);
  const [request, setRequest] = useState({});
  const [requestFinished, setRequestFinished] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const wasLoading = usePrevious(isLoading);
  const [error, setError] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [statusCode, setStatusCode] = useState(null);

  useEffect(() => {
    const executeRequest = async () => {
      const requests = Array.isArray(request) ? request : [request];
      for (let i = 0; i < requests.length; ++i) {
        const req = requests[i];
        if (!req || !req.url) {
          continue;
        }
        setError(null);
        // clear current request so that identical requests can be issued afterwards
        setRequest(null);
        setStatusCode(null);

        const method = req.method || 'GET';
        const opts = {
          method,
          headers: {...req.headers, 'Content-Type': 'application/json'},
          ...fetchOptions
        };
        if (req.body) {
          opts.body = JSON.stringify(req.body);
        }
        let url = req.url;
        if (req.query) {
          url = `${url}?${toQueryString(req.query)}`;
        }
        const asJson = req.json !== false;
        const postProcess = req.process || identity;

        setIsLoading(true);
        setAttempted(true);

        let raisedError = null;
        try {
          let resp = await fetch(url, opts);
          const statusCode = resp.status;
          setStatusCode(statusCode);
          if (resp.ok) {
            if (asJson) {
              try {
                resp = await resp.json();
              } catch (e) {
                // "e instanceof SyntaxError" does not work on Edge
                if (String(e).indexOf('SyntaxError') > -1) {
                  // JSON had no content
                  resp = null;
                } else {
                  throw new Error(e);
                }
              }
            }
            const result = postProcess(resp);
            if (req.setter) {
              req.setter(result);
            }
            setData(result);
          } else {
            // We consider application errors (4xx) to be part of normal procedure
            // and raise only server errors (5xx) to the error handler, except 401
            // (authorization is not valid currently).
            if (
              !allowedErrorCodes.includes(statusCode) &&
              (statusCode === HttpStatus.NOT_AUTHORIZED ||
                statusCode >= HttpStatus.INTERNAL_ERROR)
            ) {
              raisedError = {response: resp};
            }
            if (asJson) {
              try {
                resp = await resp.json();
              } catch (jsonError) {
                // response most likely did not contain json data
              }
            }
            setError({status: statusCode, response: resp});
          }
        } catch (err) {
          raisedError = {error: err};
          setError(err);
        }

        if (raisedError && !disableErrorHandling) {
          // Ignore error from canceled fetch on Firefox (https://bugzilla.mozilla.org/show_bug.cgi?id=1280189)
          const isFirefoxBug =
            String(raisedError.error).indexOf(
              'NetworkError when attempting to fetch'
            ) > -1;
          if (!isFirefoxBug) {
            reportError(sender, raisedError, method);
          }
        }

        setIsLoading(false);
      }
    };

    executeRequest();
    // TODO: React Hook useEffect has a missing dependency: 'allowedErrorCodes'. Either include it or remove the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, disableErrorHandling, sender]);

  const doRequest = useCallback(req => {
    setRequest(req);
  }, []);

  useEffect(() => {
    if (!isLoading && wasLoading) {
      setRequestFinished(new Date().getTime());
    }
  }, [isLoading, wasLoading]);

  return {
    data,
    isLoading,
    error,
    doRequest,
    attempted,
    setAttempted,
    statusCode,
    requestFinished
  };
};

export default useFetch;
